import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import Loading from '../components/Loading';
import { SortableTh, useSortedRows } from '../components/DataTable';
import { listPlants } from '../services/plants';
import { listParts } from '../services/parts';
import { downloadSavedInvoicePdf } from '../utils/invoiceDownload';
import { listSuppliers, getRateFor } from '../services/suppliers';
import { listTeam } from '../services/team';
import { DAY_KEYS, listTimesheets } from '../services/timesheets';
import { calcMileageDollars } from '../config/rates';
import {
  billedAdjustmentIds,
  billedTimesheetIds,
  invoiceHoldsBilling,
  listInvoices,
  updateInvoice,
  INVOICE_STATUSES,
} from '../services/invoices';
import { presetRange } from '../services/billing';
import { formatCurrency, formatDateShort } from '../utils/formatters';
import { displayFullName } from '../utils/teamDisplay';

/**
 * Plant status order — same priority list Plants.jsx uses, reused here
 * so the Sent Invoices section groups plants in the same visual rhythm
 * a Director already recognizes from the Plants index. Production
 * plants read first; Closed and "No Vehicles" sink to the bottom.
 */
const PLANT_STATUS_ORDER = [
  'Production',
  'Launch',
  'Pre-Launch',
  'Wind-Down',
  'Closed',
  'No Vehicles',
];
const PLANT_STATUS_PRIORITY = {
  'Pre-Launch': 1,
  Launch: 2,
  Production: 3,
  'Wind-Down': 0,
  Closed: -1,
};
// Mirror Plants.jsx's `effectivePlantStatus` — picks the highest-priority
// status across a plant's vehicles. Inlined here rather than imported so
// the Plants page stays the source of the original definition; if the
// priority list ever needs to change in two places it should get
// pulled into a shared util.
function effectivePlantStatus(plant) {
  const vehicles = Array.isArray(plant?.vehicles) ? plant.vehicles : [];
  let bestKey = null;
  let bestPriority = -Infinity;
  for (const v of vehicles) {
    const status = (v?.status || '').trim();
    if (!status) continue;
    const priority = PLANT_STATUS_PRIORITY[status];
    if (priority == null) continue;
    if (priority > bestPriority) {
      bestPriority = priority;
      bestKey = status;
    }
  }
  return bestKey || 'No Vehicles';
}

// Restrict the team RM / LM lookup to people currently working — same
// active-status set Plants.jsx uses. Keeps a stale leaver from
// showing up on every plant header.
const ACTIVE_TEAM_STATUSES = new Set(['Active', 'On Leave', '']);

/**
 * Invoices — Director-only. Two-section layout:
 *
 *   1. New Invoice — every supplier with approved timesheets that
 *      haven't been pulled onto a non-Draft invoice yet. One row per
 *      supplier with a "Submit Invoice" button that opens the draft
 *      preview (Phase 2 — currently a stub).
 *   2. Sent Invoices — every previously-issued invoice, grouped by
 *      plant in the same status-priority order Plants.jsx uses, with
 *      each plant header showing the Regional Manager and Customer
 *      Liaison Manager derived from the team roster.
 *
 * A filter card sits between the two sections — date range plus the
 * same Plant / Supplier / RM / Submitted By dropdowns the Timesheet
 * Approvals and Billing pages use. Filters apply only to Sent
 * Invoices; the New Invoice list is always the live "everything
 * still to bill" pool.
 */
/**
 * Invoices page. Renders one of three views depending on the `view`
 * prop (set by the wrapper component for whichever tab the user is
 * on under /invoices):
 *   • 'new'     → New Invoices tab (suppliers with unbilled work)
 *   • 'sent'    → Sent Invoices tab (existing detailed list)
 *   • 'tracker' → Invoice Tracker tab (payment-status follow-up)
 *
 * Shared loading logic + memos live in one place so all three views
 * draw from the same data without duplicating fetches. The page
 * header at the top is rendered uniformly; below it, sections are
 * gated by `view`.
 */
export default function Invoices({ view = 'sent' }) {
  const { isDirector } = useAuth();
  const navigate = useNavigate();
  const showNew = view === 'new';
  const showSent = view === 'sent';
  const showTracker = view === 'tracker';

  // ---- Loaded data --------------------------------------------------
  const [invoices, setInvoices] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRows, tsRows, supRows, plantRows, teamRows] = await Promise.all([
        listInvoices(),
        listTimesheets(),
        listSuppliers(),
        listPlants(),
        listTeam(),
      ]);
      setInvoices(invRows || []);
      setTimesheets(tsRows || []);
      setSuppliers(supRows || []);
      setPlants(plantRows || []);
      setTeam(teamRows || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Filter state (Sent Invoices only) ----------------------------
  //
  // Mirrors the Timesheet Approvals filter card exactly so a Director
  // who's used to that page lands here without re-learning anything.
  // Defaults to "This Month" so the page lands on the in-flight
  // billing period; presets and per-field pickers update both.
  const defaultRange = useMemo(() => presetRange('this-month'), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [activePreset, setActivePreset] = useState('this-month');
  const [plantFilter, setPlantFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [rmFilter, setRmFilter] = useState('');
  const [submitterFilter, setSubmitterFilter] = useState('');
  // Status filter — applies to the Sent Invoices and Invoice Tracker
  // tabs only. New Invoices rows are pre-invoice (supplier, plant)
  // buckets with no Status, so the dropdown is hidden on that tab and
  // this value is ignored there.
  const [statusFilter, setStatusFilter] = useState('');
  // PO type filter — Invoice Manager tab only. Classifies each
  // invoice by its SUPPLIER's PO setup ('No PO' / 'Blanket' /
  // 'Standard') since the PO type lives on the supplier record, not
  // the invoice.
  const [poFilter, setPoFilter] = useState('');
  // Free-text search — Invoice Tracker tab only. Matches against the
  // text the table actually displays (invoice #, supplier, plants,
  // dates, total, status), so whatever the user can see they can
  // type to find.
  const [searchQuery, setSearchQuery] = useState('');

  const applyPreset = (preset) => {
    const { startDate: s, endDate: e } = presetRange(preset);
    setStartDate(s);
    setEndDate(e);
    setActivePreset(preset);
  };

  // ---- Derived: managers by plant ----------------------------------
  //
  // Same derivation Plants.jsx uses — read the team roster, pick
  // active members with a PortalRole of RegionalManager or
  // LiaisonManager, and bucket them by the plant codes on their
  // record. Used to render the Sent Invoices section header for
  // each plant: "SHAP · RM: Jane Doe · CLM: John Smith".
  const managersByPlant = useMemo(() => {
    const map = {};
    (team || []).forEach((m) => {
      if (!ACTIVE_TEAM_STATUSES.has(m.EmploymentStatus || '')) return;
      const codes = Array.isArray(m.plantCodes) ? m.plantCodes : [];
      codes.forEach((raw) => {
        const code = (raw || '').toUpperCase();
        if (!code) return;
        if (!map[code]) map[code] = { rms: [], lms: [] };
        if (m.PortalRole === 'RegionalManager') map[code].rms.push(m);
        else if (m.PortalRole === 'LiaisonManager') map[code].lms.push(m);
      });
    });
    return map;
  }, [team]);

  const plantByCode = useMemo(() => {
    const map = new Map();
    for (const p of plants || []) {
      if (p.PlantCode) map.set(String(p.PlantCode).toUpperCase(), p);
    }
    return map;
  }, [plants]);

  const supplierByCode = useMemo(() => {
    const map = new Map();
    for (const s of suppliers || []) {
      if (s.SupplierCode) {
        map.set(String(s.SupplierCode).toUpperCase(), s);
      }
    }
    return map;
  }, [suppliers]);

  // ---- New Invoice eligibility -------------------------------------
  //
  // For each supplier, figure out which approved timesheets have rows
  // for them AND haven't been pulled onto a non-Draft invoice yet.
  // Per-(supplier, timesheet) check, not just per-timesheet, because
  // a single timesheet with rows for two suppliers needs to be
  // invoicable to each supplier independently.
  const eligibleSuppliers = useMemo(() => {
    // Set of `${supplierCodeUpper}|${timesheetId}` keys already billed
    // on a non-Draft invoice. A draft invoice doesn't count — those
    // are in-flight and can still be thrown out.
    const billedPairs = new Set();
    for (const inv of invoices || []) {
      if (!invoiceHoldsBilling(inv)) continue;
      const sc = String(inv.SupplierCode || '').trim().toUpperCase();
      if (!sc) continue;
      for (const tid of inv.timesheetIds || []) {
        billedPairs.add(`${sc}|${tid}`);
      }
    }

    // Bucket approved timesheets by supplier from their row data.
    // Adjustments (supplements / credits) need to ride along too: a
    // credit / supplement that hasn't been invoiced yet means the
    // supplier still has outstanding billing work even if all the
    // base timesheets are already covered.
    const billedAdjPairs = new Set();
    for (const inv of invoices || []) {
      if (!invoiceHoldsBilling(inv)) continue;
      const sc = String(inv.SupplierCode || '').trim().toUpperCase();
      if (!sc) continue;
      for (const aid of inv.adjustmentIds || []) {
        billedAdjPairs.add(`${sc}|${aid}`);
      }
    }

    // Normalize the filter values once so the per-timesheet / per-row
    // loops below don't keep recomputing case-folded copies. Same
    // four filters drive the Sent Invoices and Invoice Tracker tabs;
    // here they trim the pool of approved timesheets that feed the
    // (supplier, plant) buckets on the New Invoices tab.
    const isoStart = startDate || '';
    const isoEnd = endDate || '';
    const plantTarget = plantFilter ? plantFilter.toUpperCase() : '';
    const supplierTarget = supplierFilter ? supplierFilter.toUpperCase() : '';
    const rmTarget = rmFilter ? rmFilter.trim().toLowerCase() : '';

    // A timesheet's seven-day week must intersect with the picked
    // date range to count. WeekStart is the Monday; WeekStart + 6 is
    // the Sunday. Empty range = no constraint; empty WeekStart = the
    // record can't be placed on the calendar so it only passes when
    // the range is also empty (mirrors the same "unknown date"
    // handling Sent Invoices uses on IssueDate).
    const weekInRange = (t) => {
      const ws = String(t.WeekStart || '').slice(0, 10);
      if (!ws) return !isoStart && !isoEnd;
      const we = addDaysIso(ws, 6);
      if (isoStart && we < isoStart) return false;
      if (isoEnd && ws > isoEnd) return false;
      return true;
    };

    // Submitter match — id-first, name-fallback. Same lookup shape as
    // the submitterKey helper used by the Sent Invoices filter, but
    // applied directly to the timesheet record here (we have the
    // submitter on the timesheet, not on a parent invoice).
    const submitterMatches = (t) => {
      if (!submitterFilter) return true;
      const id = String(t.SubmitterId || '').trim();
      if (id && id === submitterFilter) return true;
      const name = (t.SubmitterName || '').trim().toLowerCase();
      return !!name && name === submitterFilter;
    };

    // Buckets are keyed by `${supplierCode}|${plantCode}` so each
    // (supplier, plant) pair on the New Invoice table gets its own
    // row. A supplier doing work at two plants surfaces twice — once
    // per plant — with each row carrying only that plant's hours /
    // mileage / expenses / adjustments. Rows whose timesheet entry
    // has no plantCode bucket under the placeholder '—' key so they
    // don't get silently dropped.
    const byPair = new Map();
    const ensure = (sc, pc) => {
      const plant = pc || '—';
      const key = `${sc}|${plant}`;
      if (!byPair.has(key)) {
        byPair.set(key, {
          supplierCode: sc,
          plantCode: plant,
          timesheetIds: new Set(),
          adjustmentIds: new Set(),
          totalHours: 0,
          totalLaborDollars: 0,
          totalMileage: 0,
          totalMileageDollars: 0,
          totalExpenseDollars: 0,
          // Credit dollars get SUBTRACTED from the estimated total
          // so the Total column reflects what we'd actually bill on
          // generation. Stored as a positive amount; the netting is
          // done at the end when we compute estimatedTotal.
          totalCreditDollars: 0,
        });
      }
      return byPair.get(key);
    };

    for (const t of timesheets || []) {
      if ((t.Status || '') !== 'Approved') continue;
      // Date + submitter gates run at the timesheet level — a single
      // timesheet either falls in the picked date range or it doesn't,
      // and the same person submitted every row. Plant + supplier
      // gates fire per-row inside the loop below because a single
      // timesheet can span multiple suppliers and plants.
      if (!weekInRange(t)) continue;
      if (!submitterMatches(t)) continue;
      const rows = Array.isArray(t.rows) ? t.rows : [];
      if (rows.length === 0) {
        // Rowless adjustment (credit) — bucket under its parent's
        // (supplier, plant) pair if we can find it. The credit
        // references an OriginalTimesheetId; look that up and pull
        // both the supplier code AND the plant code from the
        // parent's first row. Skip if we can't.
        if (!t.IsCredit) continue;
        const parent = (timesheets || []).find(
          (p) => String(p.id) === String(t.OriginalTimesheetId),
        );
        const parentSc = String(
          parent?.rows?.[0]?.supplierCode || '',
        )
          .trim()
          .toUpperCase();
        const parentPc = String(
          parent?.rows?.[0]?.plantCode || '',
        )
          .trim()
          .toUpperCase();
        if (!parentSc) continue;
        // Credits inherit their parent timesheet's (supplier, plant)
        // identity, so the supplier + plant filters apply to that
        // inherited pair rather than to the credit row itself.
        if (supplierTarget && parentSc !== supplierTarget) continue;
        if (plantTarget && parentPc !== plantTarget) continue;
        if (billedAdjPairs.has(`${parentSc}|${t.id}`)) continue;
        const bucket = ensure(parentSc, parentPc);
        bucket.adjustmentIds.add(String(t.id));
        // Net the credit out of the (supplier, plant) Total estimate
        // — both credit-memo and next-invoice-credit reduce what the
        // supplier owes, even though the two dispositions produce
        // different paperwork at generation time.
        bucket.totalCreditDollars += Math.abs(Number(t.CreditAmount) || 0);
        continue;
      }

      // Aggregate per-(supplier, plant) from this timesheet's rows.
      // Labor dollars are computed per-day so a row that straddles
      // a rate change bills correctly on each side — same logic the
      // Billing dashboard uses.
      const pairsTouched = new Set();
      for (const r of rows) {
        const sc = String(r.supplierCode || '').trim().toUpperCase();
        if (!sc) continue;
        // Supplier filter applies before the billed-pair skip so
        // rows for the wrong supplier never touch a bucket.
        if (supplierTarget && sc !== supplierTarget) continue;
        if (billedPairs.has(`${sc}|${t.id}`)) continue;
        const plantCode = String(r.plantCode || '').trim().toUpperCase();
        // Plant filter — empty plantCode rows never match a specific
        // plant target, so they're correctly dropped by this guard.
        if (plantTarget && plantCode !== plantTarget) continue;
        pairsTouched.add(`${sc}|${plantCode || '—'}`);
        const bucket = ensure(sc, plantCode);
        bucket.timesheetIds.add(String(t.id));
        const supplierRec = supplierByCode.get(sc);
        if (r.coverageType === 'Expenses') {
          bucket.totalExpenseDollars += Number(r.expenseAmount) || 0;
        } else if (r.coverageType === 'Mileage') {
          const values = r.values || {};
          let miles = 0;
          for (const k of Object.keys(values)) miles += Number(values[k]) || 0;
          bucket.totalMileage += miles;
        } else {
          // Labor — walk the seven day cells, look up the rate that
          // was in effect on each day, accumulate hours + dollars.
          // Missing rate (no entry in the supplier's plantAssignments
          // history) contributes hours but zero dollars — the
          // Director will see the orphan flag once they open the
          // draft preview.
          const values = r.values || {};
          for (let i = 0; i < DAY_KEYS.length; i += 1) {
            const v = Number(values[DAY_KEYS[i]]) || 0;
            if (v <= 0) continue;
            bucket.totalHours += v;
            if (supplierRec && plantCode) {
              const dayIso = addDaysIso(t.WeekStart, i);
              const rate = getRateFor(supplierRec, plantCode, dayIso);
              if (rate != null) bucket.totalLaborDollars += v * rate;
            }
          }
        }
      }
      // Supplements with rows roll up the same way regular timesheets
      // do; if this row set is itself a supplement, track its id on
      // every (supplier, plant) bucket the supplement touches so it
      // gets pulled into the right invoice at generation time.
      if (t.IsSupplement) {
        for (const pairKey of pairsTouched) {
          const [scPart] = pairKey.split('|');
          if (billedAdjPairs.has(`${scPart}|${t.id}`)) continue;
          byPair.get(pairKey)?.adjustmentIds.add(String(t.id));
        }
      }
    }

    // Finalize derived dollars per bucket — mileage dollars from the
    // miles tally, then the estimated total. The total nets credits
    // out so the column reflects what we'd actually bill if every
    // available timesheet and adjustment got pulled onto an invoice
    // right now.
    for (const bucket of byPair.values()) {
      bucket.totalMileageDollars = calcMileageDollars(bucket.totalMileage);
      bucket.estimatedTotal =
        bucket.totalLaborDollars +
        bucket.totalMileageDollars +
        bucket.totalExpenseDollars -
        bucket.totalCreditDollars;
    }

    // RM filter — applied post-bucket since the RM is derived from
    // the bucket's plant (via managersByPlant) rather than carried on
    // any individual timesheet row. 'Unassigned' picks buckets whose
    // plant has zero active Regional Managers.
    let buckets = Array.from(byPair.values());
    if (rmTarget) {
      buckets = buckets.filter((b) => {
        const rms = managersByPlant?.[b.plantCode]?.rms || [];
        if (rmTarget === 'unassigned') return rms.length === 0;
        return rms.some(
          (m) => (displayFullName(m) || '').trim().toLowerCase() === rmTarget,
        );
      });
    }

    // Drop empty buckets, attach the supplier record for display,
    // sort alphabetically by supplier name first, then by plant
    // Title (falling back to plant code), so rows for the same
    // supplier sit together in plant order.
    return buckets
      .filter(
        (b) =>
          b.timesheetIds.size > 0 ||
          b.adjustmentIds.size > 0 ||
          b.totalExpenseDollars > 0,
      )
      .map((b) => ({
        ...b,
        supplier: supplierByCode.get(b.supplierCode) || null,
      }))
      // Hide supplier codes that no longer exist on the Suppliers list
      // (renamed or removed suppliers): they'll never be billed, so they
      // shouldn't sit in New Invoices (per Jillian, Oct 2026). Skipped
      // until the Suppliers list has loaded so nothing flickers away.
      .filter((b) => supplierByCode.size === 0 || b.supplier)
      .sort((a, b) => {
        const an = (a.supplier?.Title || a.supplierCode || '').toLowerCase();
        const bn = (b.supplier?.Title || b.supplierCode || '').toLowerCase();
        const supplierCmp = an.localeCompare(bn);
        if (supplierCmp !== 0) return supplierCmp;
        const ap = (
          plantByCode.get(a.plantCode)?.Title ||
          a.plantCode ||
          ''
        ).toLowerCase();
        const bp = (
          plantByCode.get(b.plantCode)?.Title ||
          b.plantCode ||
          ''
        ).toLowerCase();
        return ap.localeCompare(bp);
      });
  }, [
    invoices,
    timesheets,
    supplierByCode,
    plantByCode,
    managersByPlant,
    startDate,
    endDate,
    plantFilter,
    supplierFilter,
    rmFilter,
    submitterFilter,
  ]);

  // Group the per-(supplier, plant) rows into ONE row per supplier, with
  // every plant listed in the Plant column (per Jillian, Aug 2026). Submit
  // Invoice already drafts per-supplier (covering all their plants), so a
  // single row per supplier matches the action. Distinct timesheets /
  // adjustments are unioned across plants; the dollar/hours columns sum.
  const eligibleBySupplier = useMemo(() => {
    const bySupplier = new Map();
    for (const row of eligibleSuppliers) {
      if (!bySupplier.has(row.supplierCode)) {
        bySupplier.set(row.supplierCode, {
          supplierCode: row.supplierCode,
          supplier: row.supplier,
          plantCodes: [],
          timesheetIds: new Set(),
          adjustmentIds: new Set(),
          totalHours: 0,
          totalLaborDollars: 0,
          totalMileage: 0,
          totalMileageDollars: 0,
          totalExpenseDollars: 0,
          totalCreditDollars: 0,
          estimatedTotal: 0,
        });
      }
      const g = bySupplier.get(row.supplierCode);
      if (
        row.plantCode &&
        row.plantCode !== '—' &&
        !g.plantCodes.includes(row.plantCode)
      ) {
        g.plantCodes.push(row.plantCode);
      }
      row.timesheetIds.forEach((id) => g.timesheetIds.add(id));
      row.adjustmentIds.forEach((id) => g.adjustmentIds.add(id));
      g.totalHours += row.totalHours || 0;
      g.totalLaborDollars += row.totalLaborDollars || 0;
      g.totalMileage += row.totalMileage || 0;
      g.totalMileageDollars += row.totalMileageDollars || 0;
      g.totalExpenseDollars += row.totalExpenseDollars || 0;
      g.totalCreditDollars += row.totalCreditDollars || 0;
      g.estimatedTotal += row.estimatedTotal || 0;
    }
    return [...bySupplier.values()]
      .map((g) => ({
        ...g,
        totalHours: Math.round(g.totalHours * 100) / 100,
        totalMileage: Math.round(g.totalMileage * 100) / 100,
        plantCodes: [...g.plantCodes].sort((a, b) => {
          const at = (plantByCode.get(a)?.Title || a).toLowerCase();
          const bt = (plantByCode.get(b)?.Title || b).toLowerCase();
          return at.localeCompare(bt);
        }),
      }))
      .sort((a, b) => {
        const an = (a.supplier?.Title || a.supplierCode || '').toLowerCase();
        const bn = (b.supplier?.Title || b.supplierCode || '').toLowerCase();
        return an.localeCompare(bn);
      });
  }, [eligibleSuppliers, plantByCode]);

  // Sorting for the "Customers to be Invoiced" table (per Jillian, Aug
  // 2026) — same SortableTh / useSortedRows pattern as the other tables.
  const newInvoiceSortConfig = useMemo(
    () => ({
      // Account # sorts numerically (108 before 394); non-numeric codes
      // sink to the bottom.
      account: (r) => {
        const n = Number(r.supplierCode);
        return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
      },
      supplier: (r) =>
        (r.supplier?.Title || r.supplierCode || '').toLowerCase(),
      plant: (r) =>
        (r.plantCodes || [])
          .map((c) => (plantByCode.get(c)?.Title || c).toLowerCase())
          .join(' | '),
      unbilled: (r) => r.timesheetIds.size,
      hours: (r) => Number(r.totalHours) || 0,
      mileage: (r) => Number(r.totalMileage) || 0,
      expenses: (r) => Number(r.totalExpenseDollars) || 0,
      adjustments: (r) => r.adjustmentIds.size,
      total: (r) => Number(r.estimatedTotal) || 0,
    }),
    [plantByCode],
  );
  const [sortedEligibleBySupplier, newInvoiceSort, onNewInvoiceSort] =
    useSortedRows(eligibleBySupplier, newInvoiceSortConfig, {
      key: 'supplier',
      dir: 'asc',
    });

  // ---- Sent Invoices filtering -------------------------------------
  //
  // Date range + the four filters. Status excludes Draft so the
  // section truly reads as "what's been sent to customers" — drafts
  // live behind the New Invoice flow until the Director generates
  // the PDF. Filters compose with each other and the date range.
  const submitterKey = useCallback((inv) => {
    // Invoices don't have a SubmitterId — they're records the
    // Director generates, not submissions a team member files. We
    // map Submitted By to the *underlying timesheets'* submitters by
    // joining via TimesheetIds. For each invoice, gather the unique
    // submitter ids from its linked timesheets; the filter passes if
    // any of them match.
    const ids = new Set();
    const tsIds = new Set((inv.timesheetIds || []).map(String));
    for (const t of timesheets || []) {
      if (!tsIds.has(String(t.id))) continue;
      const id = String(t.SubmitterId || '').trim();
      if (id) ids.add(id);
      else {
        const name = (t.SubmitterName || '').trim().toLowerCase();
        if (name) ids.add(name);
      }
    }
    return ids;
  }, [timesheets]);

  const sentInvoices = useMemo(() => {
    const isoStart = startDate || '';
    const isoEnd = endDate || '';
    const plantTarget = plantFilter ? plantFilter.toUpperCase() : '';
    const supplierTarget = supplierFilter
      ? supplierFilter.toUpperCase()
      : '';
    const rmTarget = rmFilter ? rmFilter.trim().toLowerCase() : '';
    return (invoices || [])
      .filter((inv) => (inv.Status || '') !== 'Draft')
      .filter((inv) => {
        // Date filter — on IssueDate so a Director picking "This
        // Month" sees invoices issued this month regardless of the
        // billing period each invoice covers. (Period and issue
        // date can differ — e.g. an April invoice issued May 5.)
        const issue = (inv.IssueDate || '').slice(0, 10);
        if (!issue) return !isoStart && !isoEnd;
        if (isoStart && issue < isoStart) return false;
        if (isoEnd && issue > isoEnd) return false;
        return true;
      })
      .filter((inv) => {
        if (!plantTarget) return true;
        return (inv.plantCodes || []).some(
          (c) => String(c).toUpperCase() === plantTarget,
        );
      })
      .filter((inv) => {
        if (!supplierTarget) return true;
        return (
          String(inv.SupplierCode || '').toUpperCase() === supplierTarget
        );
      })
      .filter((inv) => {
        if (!rmTarget) return true;
        const stored = (inv.RegionalManager || '').trim().toLowerCase();
        if (rmTarget === 'unassigned') return stored === '';
        return stored === rmTarget;
      })
      .filter((inv) => {
        if (!submitterFilter) return true;
        return submitterKey(inv).has(submitterFilter);
      })
      .filter((inv) => {
        if (!statusFilter) return true;
        return (inv.Status || '') === statusFilter;
      })
      .filter((inv) => {
        // PO type — derived from the invoice's supplier record. POs
        // live per plant on the supplier's plant assignments, so an
        // invoice matches a type when any of its supplier's plants
        // holds a PO of that type. The legacy supplier-level PoType
        // is kept as a fallback for records saved before the move.
        if (!poFilter) return true;
        const s = supplierByCode.get(
          String(inv.SupplierCode || '').toUpperCase(),
        );
        const onPo = s?.PoRequired === 'Yes';
        if (poFilter === 'none') return !onPo;
        const poTypes = new Set(
          (s?.plantAssignments || [])
            .map((pa) => pa?.po?.type)
            .filter(Boolean),
        );
        if (s?.PoType) poTypes.add(s.PoType);
        if (poFilter === 'blanket') {
          return onPo && poTypes.has('Blanket PO');
        }
        if (poFilter === 'standard') {
          return onPo && poTypes.has('Monthly PO');
        }
        return true;
      });
  }, [
    invoices,
    startDate,
    endDate,
    plantFilter,
    supplierFilter,
    rmFilter,
    submitterFilter,
    statusFilter,
    poFilter,
    supplierByCode,
    submitterKey,
  ]);

  // Group sent invoices by plant. Multi-plant invoices appear under
  // every plant they touch — same pattern the Timesheet Approvals
  // page uses for cross-plant timesheets. Plant order follows the
  // status-priority list Plants.jsx uses; plants with no invoices
  // in the current filtered window don't render a section.
  const invoicesByPlant = useMemo(() => {
    const map = new Map();
    for (const inv of sentInvoices) {
      const plantCodes = Array.isArray(inv.plantCodes) ? inv.plantCodes : [];
      const targets =
        plantCodes.length > 0 ? plantCodes : ['(No plant)'];
      for (const raw of targets) {
        const code = String(raw || '').toUpperCase();
        if (!map.has(code)) map.set(code, []);
        map.get(code).push(inv);
      }
    }
    // Sort each plant's invoice list by IssueDate desc so the most
    // recent invoice reads first.
    for (const list of map.values()) {
      list.sort((a, b) => {
        const ad = a.IssueDate || a.Created || '';
        const bd = b.IssueDate || b.Created || '';
        return bd.localeCompare(ad);
      });
    }
    // Sort plants by status priority, then alpha within each
    // priority bucket.
    return Array.from(map.entries()).sort((a, b) => {
      const ap = plantByCode.get(a[0]);
      const bp = plantByCode.get(b[0]);
      const aStatus = ap ? effectivePlantStatus(ap) : 'No Vehicles';
      const bStatus = bp ? effectivePlantStatus(bp) : 'No Vehicles';
      const ai = PLANT_STATUS_ORDER.indexOf(aStatus);
      const bi = PLANT_STATUS_ORDER.indexOf(bStatus);
      if (ai !== bi) return ai - bi;
      return a[0].localeCompare(b[0]);
    });
  }, [sentInvoices, plantByCode]);

  // ---- RM dropdown options -----------------------------------------
  //
  // Union of two sources so the dropdown covers every tab's filter
  // logic:
  //   1. Stored RegionalManager names on past invoices (Sent +
  //      Tracker filters look up the saved string).
  //   2. Active Regional Managers on the team roster (New Invoices
  //      filter resolves RM via managersByPlant for the bucket's
  //      plant, so we want their display names available even before
  //      any invoice has been generated naming them).
  const rmOptions = useMemo(() => {
    const set = new Set();
    for (const inv of invoices || []) {
      if ((inv.Status || '') === 'Draft') continue;
      const rm = (inv.RegionalManager || '').trim();
      if (rm) set.add(rm);
      else set.add('Unassigned');
    }
    for (const m of team || []) {
      if (!ACTIVE_TEAM_STATUSES.has(m.EmploymentStatus || '')) continue;
      if (m.PortalRole !== 'RegionalManager') continue;
      const name = (displayFullName(m) || '').trim();
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
  }, [invoices, team]);

  // Submitter options — union of two sources:
  //   1. Submitters on timesheets that have already been invoiced
  //      (covers Sent + Tracker tabs).
  //   2. Submitters on approved timesheets — those are the ones
  //      feeding the eligibleSuppliers list on the New Invoices tab,
  //      so they need to be picked even before any invoice naming
  //      them has been generated.
  const submitterOptions = useMemo(() => {
    const map = new Map();
    const addFrom = (t) => {
      const id = String(t.SubmitterId || '').trim();
      const name = (t.SubmitterName || '').trim();
      const key = id || (name ? name.toLowerCase() : '');
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, { value: key, label: name || '(unnamed)' });
      }
    };
    const tsById = new Map((timesheets || []).map((t) => [String(t.id), t]));
    for (const inv of invoices || []) {
      if ((inv.Status || '') === 'Draft') continue;
      for (const tid of inv.timesheetIds || []) {
        const t = tsById.get(String(tid));
        if (t) addFrom(t);
      }
    }
    for (const t of timesheets || []) {
      if ((t.Status || '') !== 'Approved') continue;
      addFrom(t);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
  }, [invoices, timesheets]);

  // ---- Render guards ------------------------------------------------
  if (!isDirector) {
    return (
      <div className="card">
        <div className="card-title">Invoices</div>
        <p className="muted">
          Only Directors can view and generate invoices. Talk to a
          Director if you need a copy of a specific invoice.
        </p>
      </div>
    );
  }
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load Invoices</div>
        <p>{error.message || 'Unknown error.'}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1>
          {showNew
            ? 'New Invoices'
            : showSent
              ? 'Sent Invoices'
              : 'Invoice Manager'}
        </h1>
        <div className="actions">
          {showNew && (
            <button
              className="btn btn-outline"
              onClick={() => navigate('/invoices/blank')}
            >
              Create Blank Invoice
            </button>
          )}
          <button className="btn btn-outline" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      {/* ---------- Filter card (date + 4 filters) ----------
          Filter card sits at the top of all three Invoices tabs (New
          Invoices, Sent Invoices, Invoice Tracker) — same fields, same
          defaults. The filters drive:
            • New Invoices  → which approved-but-unbilled timesheets
                              get rolled into the per-(supplier, plant)
                              rows in eligibleSuppliers
            • Sent Invoices → which past invoices show in the RM →
                              Plant grouped detail view
            • Tracker       → which invoices appear in the flat
                              payment-status table */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <PresetButton preset="this-month" active={activePreset} onClick={applyPreset}>
            This Month
          </PresetButton>
          <PresetButton preset="last-month" active={activePreset} onClick={applyPreset}>
            Last Month
          </PresetButton>
          <PresetButton preset="this-week" active={activePreset} onClick={applyPreset}>
            This Week
          </PresetButton>
          <PresetButton preset="last-week" active={activePreset} onClick={applyPreset}>
            Last Week
          </PresetButton>
          <PresetButton preset="past-4-weeks" active={activePreset} onClick={applyPreset}>
            Past 4 Weeks
          </PresetButton>
          <PresetButton preset="past-5-weeks" active={activePreset} onClick={applyPreset}>
            Past 5 Weeks
          </PresetButton>
          <PresetButton preset="ytd" active={activePreset} onClick={applyPreset}>
            Year to Date
          </PresetButton>
          <PresetButton preset="last-year" active={activePreset} onClick={applyPreset}>
            Last Year
          </PresetButton>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <DateInput
            label="From"
            value={startDate}
            onChange={(v) => {
              setStartDate(v);
              setActivePreset(null);
            }}
          />
          <DateInput
            label="To"
            value={endDate}
            onChange={(v) => {
              setEndDate(v);
              setActivePreset(null);
            }}
          />
          <div
            className="muted"
            style={{ marginLeft: 'auto', fontSize: 13, alignSelf: 'center' }}
          >
            {formatRangeLabel(startDate, endDate)}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            marginTop: 12,
            borderTop: '1px solid var(--brand-border, #e5e7eb)',
            paddingTop: 12,
          }}
        >
          <DropdownFilter
            label="Plant"
            value={plantFilter}
            onChange={setPlantFilter}
            allLabel="All plants"
            options={plants
              .filter((p) => p.PlantCode)
              .slice()
              .sort((a, b) =>
                String(a.PlantCode).localeCompare(String(b.PlantCode)),
              )
              .map((p) => ({
                value: p.PlantCode,
                label: p.Title
                  ? `${p.PlantCode} — ${p.Title}`
                  : p.PlantCode,
              }))}
          />
          <DropdownFilter
            label="Supplier"
            value={supplierFilter}
            onChange={setSupplierFilter}
            allLabel="All suppliers"
            options={suppliers
              .filter((s) => s.SupplierCode)
              .slice()
              .sort((a, b) => {
                const an = (a.Title || a.SupplierCode || '').toLowerCase();
                const bn = (b.Title || b.SupplierCode || '').toLowerCase();
                return an.localeCompare(bn);
              })
              .map((s) => ({
                value: s.SupplierCode,
                label: s.Title
                  ? `${s.SupplierCode} — ${s.Title}`
                  : s.SupplierCode,
              }))}
          />
          <DropdownFilter
            label="Regional Manager"
            value={rmFilter}
            onChange={setRmFilter}
            allLabel="All Regional Managers"
            options={rmOptions.map((rm) => ({ value: rm, label: rm }))}
            minWidth={220}
          />
          <DropdownFilter
            label="Submitted By"
            value={submitterFilter}
            onChange={setSubmitterFilter}
            allLabel="All submitters"
            options={submitterOptions}
            minWidth={220}
          />
          {/* Status filter — Invoice Tracker tab only. New Invoices
              rows have no invoice status yet, and the Sent Invoices
              tab doesn't need it (per Jillian, June 2026). Draft is
              excluded from the options because drafts are filtered
              out upstream. */}
          {showTracker && (
            <DropdownFilter
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allLabel="All statuses"
              options={INVOICE_STATUSES.filter((s) => s !== 'Draft').map(
                (s) => ({ value: s, label: s }),
              )}
              minWidth={180}
            />
          )}
          {/* PO type filter — Invoice Manager tab only. Options map to
              the supplier record's PO setup; see the sentInvoices
              memo for the classification rule. */}
          {showTracker && (
            <DropdownFilter
              label="PO Number"
              value={poFilter}
              onChange={setPoFilter}
              allLabel="All POs"
              options={[
                { value: 'none', label: 'No PO' },
                { value: 'blanket', label: 'Blanket' },
                { value: 'standard', label: 'Monthly' },
              ]}
              minWidth={150}
            />
          )}
          {/* Free-text search — Invoice Tracker only. Searches every
              column the table displays. */}
          {showTracker && (
            // flex: 1 so the search field grows to fill the rest of its
            // row and its right edge lines up with the right side of the
            // filter panel (the Regional Manager field on the row above).
            // Per Jillian, Aug 2026.
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                flex: '1 1 220px',
              }}
            >
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--brand-muted)',
                }}
              >
                Search
              </label>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search anything…"
                style={{
                  // Strip the browser's native "search field" chrome
                  // (Safari especially renders type=search taller and
                  // rounder than a text input) and pin the height to
                  // the same 36px the filter dropdowns use so the row
                  // reads as one consistent set of controls.
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  boxSizing: 'border-box',
                  width: '100%',
                  height: 36,
                  margin: 0,
                  padding: '0 10px',
                  border: '1px solid var(--brand-border)',
                  borderRadius: 6,
                  fontSize: 14,
                  minWidth: 220,
                }}
              />
            </div>
          )}
          {(plantFilter ||
            supplierFilter ||
            rmFilter ||
            submitterFilter ||
            statusFilter ||
            poFilter ||
            searchQuery) && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                setPlantFilter('');
                setSupplierFilter('');
                setRmFilter('');
                setSubmitterFilter('');
                setStatusFilter('');
                setPoFilter('');
                setSearchQuery('');
              }}
              style={{ alignSelf: 'flex-end', marginBottom: 1 }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ---------- New Invoice ----------
          Surfaces every supplier with at least one approved
          timesheet (or pending supplement/credit) that hasn't been
          billed on a non-Draft invoice yet. Click Submit Invoice on
          a row to open the draft preview. Only rendered on the
          New Invoices tab. Filter card above applies the same
          date / plant / supplier / RM / submitter filters used on
          the other two tabs — the filtered timesheet pool drives
          the eligibleSuppliers memo. */}
      {showNew && (
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Customers to be Invoiced</h2>
        {eligibleBySupplier.length === 0 ? (
          <div className="card">
            <p className="muted" style={{ margin: 0 }}>
              No suppliers have unbilled approved timesheets right now.
              New invoices will show up here as timesheets get approved.
            </p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <table className="mini-table">
              <thead>
                <tr>
                  <SortableTh
                    sortKey="account"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                    width={60}
                  >
                    #
                  </SortableTh>
                  <SortableTh
                    sortKey="supplier"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                  >
                    Supplier
                  </SortableTh>
                  <SortableTh
                    sortKey="plant"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                  >
                    Plant
                  </SortableTh>
                  <SortableTh
                    sortKey="unbilled"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                    align="right"
                    width={130}
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>Unbilled Weeks</span>
                  </SortableTh>
                  <SortableTh
                    sortKey="hours"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                    align="right"
                  >
                    Hours
                  </SortableTh>
                  <SortableTh
                    sortKey="mileage"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                    align="right"
                  >
                    Mileage
                  </SortableTh>
                  <SortableTh
                    sortKey="expenses"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                    align="right"
                  >
                    Expenses
                  </SortableTh>
                  <SortableTh
                    sortKey="adjustments"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                    align="right"
                  >
                    Adjustments
                  </SortableTh>
                  <SortableTh
                    sortKey="total"
                    currentSort={newInvoiceSort}
                    onSort={onNewInvoiceSort}
                    align="right"
                  >
                    Total
                  </SortableTh>
                  <th style={{ width: 160 }}></th>
                </tr>
              </thead>
              <tbody>
                {sortedEligibleBySupplier.map((row) => (
                  <tr key={row.supplierCode}>
                    <td>
                      <Link
                        to={`/suppliers/${encodeURIComponent(row.supplierCode)}`}
                        style={{ color: 'var(--brand-ink)' }}
                      >
                        {row.supplierCode}
                      </Link>
                    </td>
                    <td>
                      {row.supplier ? (
                        <Link
                          to={`/suppliers/${encodeURIComponent(row.supplierCode)}`}
                          style={{ color: 'var(--brand-ink)' }}
                        >
                          {row.supplier.Title}
                        </Link>
                      ) : (
                        <span>
                          <em className="muted" style={{ fontSize: 11 }}>
                            (not in Suppliers list)
                          </em>
                        </span>
                      )}
                    </td>
                    <td>
                      {/* Plant cell — all plants this supplier has unbilled
                          work at, stacked one per line (per Jillian, Aug
                          2026). Resolve each code to its display Title via
                          plantByCode, falling back to the raw code. */}
                      {row.plantCodes.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {row.plantCodes.map((code) => (
                            <span key={code}>
                              {plantByCode.get(code)?.Title || code}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.timesheetIds.size}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.totalHours
                        ? row.totalHours.toFixed(2)
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.totalMileage
                        ? `${row.totalMileage} mi`
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.totalExpenseDollars
                        ? formatCurrency(row.totalExpenseDollars)
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.adjustmentIds.size > 0 ? (
                        <span>{row.adjustmentIds.size}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td
                      style={{ textAlign: 'right', fontWeight: 600 }}
                      title="Estimated total if invoiced now — labor priced at the supplier's per-day rate, plus mileage and expenses, less any credits."
                    >
                      {row.estimatedTotal
                        ? formatCurrency(row.estimatedTotal)
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() =>
                          navigate(
                            `/invoices/draft/${encodeURIComponent(row.supplierCode)}`,
                          )
                        }
                      >
                        Submit Invoice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* ---------- Sent Invoices — detailed RM → Plant grouped view */}
      {showSent && (
      <section>
        <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Sent Invoices</h2>
        {invoicesByPlant.length === 0 ? (
          <div className="card">
            <p className="muted" style={{ margin: 0 }}>
              No invoices match the current filters. Try widening the
              date range or clearing a filter.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {invoicesByPlant.map(([code, invList]) => {
              const plant = plantByCode.get(code);
              const managers = managersByPlant[code] || {
                rms: [],
                lms: [],
              };
              return (
                <Fragment key={code}>
                  <PlantSectionHeader
                    plantCode={code}
                    plant={plant}
                    managers={managers}
                    count={invList.length}
                  />
                  <div
                    className="card"
                    style={{ padding: 0, marginTop: -10 }}
                  >
                    <table className="mini-table">
                      <thead>
                        <tr>
                          <th>Invoice #</th>
                          <th>Supplier</th>
                          <th>Period</th>
                          <th>Issued</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                          <th style={{ width: 110 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invList.map((inv) => (
                          <tr key={`${code}-${inv.id}`}>
                            <td>
                              {/* Click through to the editable invoice
                                  detail page. */}
                              <Link
                                to={`/invoices/view/${encodeURIComponent(inv.id)}`}
                                style={{ fontWeight: 600 }}
                              >
                                {inv.InvoiceNumber || inv.id}
                              </Link>
                            </td>
                            <td>
                              {inv.SupplierName || inv.SupplierCode || '—'}
                            </td>
                            <td>
                              {inv.PeriodStart && inv.PeriodEnd
                                ? `${formatDateShort(inv.PeriodStart)} – ${formatDateShort(inv.PeriodEnd)}`
                                : '—'}
                            </td>
                            <td>
                              {inv.IssueDate
                                ? formatDateShort(inv.IssueDate)
                                : '—'}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {Number.isFinite(Number(inv.Total))
                                ? formatCurrency(Number(inv.Total))
                                : '—'}
                            </td>
                            <td>
                              <StatusBadge status={inv.Status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Fragment>
              );
            })}
          </div>
        )}
      </section>
      )}

      {/* ---------- Invoice Tracker — payment-status follow-up view.
          Flat table listing every invoice with an inline status
          dropdown so the Director can move records through Draft →
          Sent → Awaiting Payment → Paid (or Overdue / Cancelled) as
          they get acknowledgements / payments by email. Same filter
          card above. */}
      {showTracker && (
      <InvoiceTrackerTable
        invoices={sentInvoices}
        plantByCode={plantByCode}
        supplierByCode={supplierByCode}
        searchQuery={searchQuery}
        onNotesChange={async (id, notes) => {
          // Persist the quick note, then patch the row in place —
          // no full reload needed for a one-field edit, so the
          // table doesn't flicker while someone is jotting notes
          // across several rows. Same Notes field the invoice
          // detail page edits.
          try {
            await updateInvoice(id, { Notes: notes });
            setInvoices((prev) =>
              prev.map((inv) =>
                String(inv.id) === String(id)
                  ? { ...inv, Notes: notes }
                  : inv,
              ),
            );
          } catch (err) {
            setError(err);
          }
        }}
        onStatusChange={async (id, newStatus) => {
          // Persist the new status and refresh in place so the user
          // sees the row update immediately. Errors surface in the
          // shared error banner below.
          try {
            await updateInvoice(id, { Status: newStatus });
            await load();
          } catch (err) {
            setError(err);
          }
        }}
      />
      )}
    </>
  );
}

// ---- Helpers / sub-components ----------------------------------------

function PlantSectionHeader({ plantCode, plant, managers, count }) {
  // Soft-orange brand-primary banner — same treatment Plants.jsx
  // uses for its plant section dividers. Surfaces the plant code +
  // name on the left, the RM / Customer Liaison Manager on the
  // right, and the invoice count at the far right for at-a-glance
  // load reading.
  const rmNames = managers.rms.map(displayFullName).filter(Boolean).join(', ');
  const lmNames = managers.lms.map(displayFullName).filter(Boolean).join(', ');
  return (
    <div
      style={{
        background: 'var(--brand-primary-soft, #fff4e6)',
        color: 'var(--brand-primary-dark, #b14a14)',
        padding: '10px 14px',
        borderRadius: 6,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Link
          to={
            plant ? `/plants/${encodeURIComponent(plant.PlantCode)}` : '#'
          }
          style={{ textDecoration: 'none' }}
        >
          <span className="badge plant">{plantCode}</span>
        </Link>
        <strong style={{ fontWeight: 600 }}>
          {plant?.Title || '(plant record missing)'}
        </strong>
        <span style={{ color: 'var(--brand-muted)', fontSize: 12 }}>
          {rmNames ? `RM: ${rmNames}` : 'RM: —'}
          {' · '}
          {lmNames ? `CLM: ${lmNames}` : 'CLM: —'}
        </span>
      </div>
      <span style={{ fontWeight: 400, fontSize: 12 }}>
        {count} {count === 1 ? 'invoice' : 'invoices'}
      </span>
    </div>
  );
}

// Shared palette for invoice statuses — used by both the read-only
// StatusBadge in the Sent Invoices section and the inline status
// dropdown in the Invoice Tracker. Adding a status here is enough to
// give it consistent styling in both surfaces; INVOICE_STATUSES drives
// the list of options the dropdown offers.
const STATUS_PALETTE = {
  Draft: { bg: 'rgba(100, 116, 139, 0.15)', fg: '#475569' },
  Sent: { bg: 'rgba(202, 138, 4, 0.12)', fg: '#ca8a04' },
  'Awaiting Payment': {
    bg: 'rgba(202, 138, 4, 0.12)',
    fg: '#ca8a04',
  },
  'Partial Payment': {
    bg: 'rgba(13, 148, 136, 0.12)',
    fg: '#0d9488',
  },
  Paid: {
    bg: 'rgba(22, 163, 74, 0.12)',
    fg: 'var(--brand-success, #16a34a)',
  },
  Overpaid: {
    bg: 'rgba(37, 99, 235, 0.12)',
    fg: '#2563eb',
  },
  'Duplicate Pay': {
    bg: 'rgba(147, 51, 234, 0.12)',
    fg: '#9333ea',
  },
  Overdue: {
    bg: 'rgba(220, 38, 38, 0.12)',
    fg: 'var(--brand-danger)',
  },
  Cancelled: {
    bg: 'rgba(100, 116, 139, 0.15)',
    fg: '#475569',
  },
};

function StatusBadge({ status }) {
  const { bg, fg } = STATUS_PALETTE[status] || STATUS_PALETTE.Draft;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        background: bg,
        color: fg,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {status || 'Draft'}
    </span>
  );
}

/**
 * InvoiceTrackerTable
 * -------------------
 * Flat table used by the Invoice Tracker tab. One row per invoice with
 * an inline status `<select>` so the Director can move records through
 * the payment lifecycle (Sent → Awaiting Payment → Paid, or Overdue /
 * Cancelled) as they get acknowledgements and payments by email.
 *
 * Drafts are filtered out upstream — `sentInvoices` already drops them
 * — so the dropdown lists every INVOICE_STATUS value but the table
 * itself only shows invoices that have been issued.
 *
 * onStatusChange receives the invoice id and the new status; the
 * parent persists via updateInvoice and reloads so the row re-renders
 * with the saved value.
 */
function InvoiceTrackerTable({
  invoices,
  plantByCode,
  supplierByCode,
  searchQuery,
  onNotesChange,
  onStatusChange,
}) {
  if (!invoices || invoices.length === 0) {
    return (
      <section>
        <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>All Invoices</h2>
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No invoices match the current filters. Try widening the
            date range or clearing a filter.
          </p>
        </div>
      </section>
    );
  }

  return (
    <InvoiceTrackerTableInner
      invoices={invoices}
      plantByCode={plantByCode}
      supplierByCode={supplierByCode}
      searchQuery={searchQuery}
      onNotesChange={onNotesChange}
      onStatusChange={onStatusChange}
    />
  );
}

/**
 * Inner table — split out so the sortable-rows hook (which must run
 * unconditionally) sits below the empty-state early return in the
 * wrapper above. Every column is sortable: a three-state click cycle
 * (ascending → descending → cleared) via SortableTh / useSortedRows,
 * the same UX the Plant and Supplier detail tables use. Default sort is
 * Issued, newest first, matching the previous static ordering.
 */
function InvoiceTrackerTableInner({
  invoices,
  plantByCode,
  supplierByCode,
  searchQuery,
  onNotesChange,
  onStatusChange,
}) {
  // Enrich each row with the display labels once so the sort comparators
  // and the cell render agree on the exact text being compared.
  const rows = useMemo(() => {
    const enriched = (invoices || []).map((inv) => {
      const sc = String(inv.SupplierCode || '').toUpperCase();
      const supplier = supplierByCode?.get(sc);
      const supplierLabel =
        inv.SupplierName || supplier?.Title || inv.SupplierCode || '—';
      const plantCodes = Array.isArray(inv.plantCodes)
        ? inv.plantCodes
        : [];
      const plantLabel =
        plantCodes.length === 0
          ? '—'
          : plantCodes
              .map((c) => String(c || '').toUpperCase())
              .join(', ');
      return { ...inv, _supplierLabel: supplierLabel, _plantLabel: plantLabel };
    });

    // Free-text search across every column the table shows. Built
    // from the same enriched labels and the same formatters the cells
    // render with, so "what you see is what you can search" — e.g.
    // searching "$1,2" or "Jun 3" or "Awaiting" all work. The raw
    // ISO dates and unformatted total are included too so "2026-06"
    // and "1234.56" also match.
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return enriched;
    return enriched.filter((inv) => {
      const haystack = [
        inv.InvoiceNumber || inv.id,
        inv._supplierLabel,
        inv._plantLabel,
        inv.IssueDate,
        inv.IssueDate ? formatDateShort(inv.IssueDate) : '',
        inv.DueDate,
        inv.DueDate ? formatDateShort(inv.DueDate) : '',
        Number.isFinite(Number(inv.Total)) ? String(inv.Total) : '',
        Number.isFinite(Number(inv.Total))
          ? formatCurrency(Number(inv.Total))
          : '',
        inv.Status,
        inv.Notes,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' | ');
      return haystack.includes(q);
    });
  }, [invoices, supplierByCode, plantByCode, searchQuery]);

  const sortConfig = useMemo(
    () => ({
      invoiceNumber: (r) => r.InvoiceNumber || String(r.id || ''),
      supplier: (r) => (r._supplierLabel || '').toLowerCase(),
      plants: (r) => (r._plantLabel || '').toLowerCase(),
      issued: (r) => r.IssueDate || r.Created || '',
      due: (r) => r.DueDate || '',
      total: (r) => {
        const n = Number(r.Total);
        return Number.isFinite(n) ? n : null;
      },
      status: (r) => r.Status || '',
    }),
    [],
  );

  // Default: most recently issued first (Issued descending).
  const [sortedRows, sort, onSort] = useSortedRows(rows, sortConfig, {
    key: 'issued',
    dir: 'desc',
  });

  return (
    <section>
      <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>All Invoices</h2>
      <div className="card" style={{ padding: 0 }}>
        <table className="mini-table">
          <thead>
            <tr>
              <SortableTh sortKey="invoiceNumber" currentSort={sort} onSort={onSort}>
                Invoice #
              </SortableTh>
              <SortableTh sortKey="supplier" currentSort={sort} onSort={onSort}>
                Supplier
              </SortableTh>
              <SortableTh sortKey="plants" currentSort={sort} onSort={onSort}>
                Plants
              </SortableTh>
              <SortableTh sortKey="issued" currentSort={sort} onSort={onSort}>
                Issued
              </SortableTh>
              <SortableTh sortKey="due" currentSort={sort} onSort={onSort}>
                Due
              </SortableTh>
              <SortableTh sortKey="total" currentSort={sort} onSort={onSort} align="right">
                Total
              </SortableTh>
              <SortableTh sortKey="status" currentSort={sort} onSort={onSort} width={200}>
                Status
              </SortableTh>
              <th>PDF</th>
              <th style={{ minWidth: 200 }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((inv) => (
              <tr key={inv.id}>
                <td>
                  {/* Click through to the editable invoice detail page. */}
                  <Link
                    to={`/invoices/view/${encodeURIComponent(inv.id)}`}
                    style={{ fontWeight: 600 }}
                  >
                    {inv.InvoiceNumber || inv.id}
                  </Link>
                </td>
                <td>{inv._supplierLabel}</td>
                <td>{inv._plantLabel}</td>
                <td>{inv.IssueDate ? formatDateShort(inv.IssueDate) : '—'}</td>
                <td>{inv.DueDate ? formatDateShort(inv.DueDate) : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {Number.isFinite(Number(inv.Total))
                    ? formatCurrency(Number(inv.Total))
                    : '—'}
                </td>
                <td>
                  <StatusSelect
                    value={inv.Status || 'Sent'}
                    onChange={(next) => onStatusChange(inv.id, next)}
                  />
                </td>
                <td>
                  <DownloadPdfButton
                    invoice={inv}
                    supplier={supplierByCode?.get(
                      String(inv.SupplierCode || '').toUpperCase(),
                    )}
                    plantByCode={plantByCode}
                  />
                </td>
                <td>
                  <NotesCell
                    value={inv.Notes || ''}
                    onSave={(next) => onNotesChange(inv.id, next)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Parts are only needed to back-fill part numbers on older invoices'
// PDFs, so they're fetched once, the first time Download is clicked.
let partsPromise = null;
function loadPartsOnce() {
  if (!partsPromise) {
    partsPromise = listParts().catch((err) => {
      partsPromise = null;
      throw err;
    });
  }
  return partsPromise;
}

/**
 * "Download" button on each All Invoices row (Oct 2026): downloads that
 * invoice's PDF without opening it. Icon and label stay on one line.
 */
function DownloadPdfButton({ invoice, supplier, plantByCode }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      let parts = [];
      try {
        parts = await loadPartsOnce();
      } catch {
        parts = [];
      }
      await downloadSavedInvoicePdf(invoice, { supplier, plantByCode, parts });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[invoices] PDF download failed', err);
      window.alert(`Couldn't download this PDF: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className="btn btn-outline btn-sm"
      onClick={onClick}
      disabled={busy}
      title={`Download ${invoice.InvoiceNumber || 'invoice'} as a PDF`}
      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 4v11" />
        <path d="M7 10l5 5 5-5" />
        <path d="M5 20h14" />
      </svg>
      {busy ? 'Preparing…' : 'Download'}
    </button>
  );
}

/**
 * NotesCell — inline quick-notes input on the All Invoices table.
 * Edits buffer locally while typing; the save fires on blur (or
 * Enter) and only when the text actually changed, so tabbing
 * through rows doesn't generate writes. Escape reverts to the
 * last-saved value. Same Notes field the invoice detail page edits.
 */
function NotesCell({ value, onSave }) {
  const [draft, setDraft] = useState(value);
  // Resync when the saved value changes underneath us (e.g. the row
  // re-rendered after a save or an edit on the detail page).
  useEffect(() => {
    setDraft(value);
  }, [value]);
  const commit = () => {
    if (draft !== value) onSave(draft);
  };
  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') setDraft(value);
      }}
      placeholder="Add a note…"
      title={draft || 'Add a note…'}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        // Pinned to the same 22px as the Status dropdown beside it
        // so the controls match the row's text height.
        height: 22,
        padding: '0 8px',
        border: '1px solid var(--brand-border)',
        borderRadius: 4,
        fontSize: 13,
        background: '#fff',
      }}
    />
  );
}

/**
 * StatusSelect — the inline dropdown the Director uses to move an
 * invoice through the payment lifecycle. Styled to match the status
 * badge palette so the dropdown carries the same color cue you'd see
 * on a read-only badge.
 */
function StatusSelect({ value, onChange }) {
  const { bg, fg } = STATUS_PALETTE[value] || STATUS_PALETTE.Draft;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        // Explicit height (shared with the Notes input beside it on
        // the All Invoices table) so the two controls line up and
        // match the row's text height.
        boxSizing: 'border-box',
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        border: `1px solid ${fg}`,
        background: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 600,
        minWidth: 170,
      }}
    >
      {INVOICE_STATUSES.map((s) => (
        <option key={s} value={s} style={{ background: '#fff', color: '#000' }}>
          {s}
        </option>
      ))}
    </select>
  );
}

function PresetButton({ preset, active, onClick, children }) {
  const isActive = active === preset;
  return (
    <button
      type="button"
      className={isActive ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
      aria-pressed={isActive}
      onClick={() => onClick(preset)}
    >
      {children}
    </button>
  );
}

function DateInput({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--brand-muted)',
        }}
      >
        {label}
      </label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: '8px 10px',
          border: '1px solid var(--brand-border)',
          borderRadius: 6,
          fontSize: 14,
        }}
      />
    </div>
  );
}

function DropdownFilter({
  label,
  value,
  onChange,
  options,
  allLabel,
  minWidth = 240,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--brand-muted)',
        }}
      >
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          // Explicit height so every filter control (dropdowns + the
          // tracker search box) sits at the same 36px and the row's
          // labels line up.
          boxSizing: 'border-box',
          height: 36,
          padding: '0 10px',
          border: '1px solid var(--brand-border)',
          borderRadius: 6,
          fontSize: 14,
          minWidth,
        }}
      >
        <option value="">{allLabel}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function formatRangeLabel(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const fmt = (iso) => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  return `${fmt(startIso)} – ${fmt(endIso)}`;
}

// ISO-date-aware day arithmetic — used to look up the rate that was
// in effect on each day of a row when computing labor dollars for
// the Total column. Parses the string components rather than
// constructing a Date so we don't shift the day in negative-offset
// timezones.
function addDaysIso(iso, days) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

// `billedTimesheetIds` and `billedAdjustmentIds` are imported from the
// service layer above but not used in this Phase 1 render — they're
// the helper functions Phase 2's Submit Invoice flow will use to
// finalize an invoice's TimesheetIds list. Keeping them imported here
// so the eventual wiring is one less import to remember.
// eslint-disable-next-line no-unused-vars
const _unused = { billedTimesheetIds, billedAdjustmentIds };
